import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { DateManager } from 'src/app/core/managers/date.manager';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { SaasTransportCompany } from 'src/app/core/interfaces/saasTransportCompany';
import { Utils } from 'src/app/core/resources/utils';
import { InsurancePolicyService } from 'src/app/core/services/insurance-policy.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { CompaniesService } from '../list-companies.service';

export interface SaasTransportCompanyPolicyFormData {
  transportCompany: SaasTransportCompany;
}

@Component({
  selector: 'app-saas-transport-company-policy-form',
  templateUrl: './saas-transport-company-policy-form.component.html',
  styleUrls: ['./saas-transport-company-policy-form.component.scss']
})
export class SaasTransportCompanyPolicyFormComponent implements OnInit {
  form: FormGroup;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<SaasTransportCompanyPolicyFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SaasTransportCompanyPolicyFormData,
    public utils: Utils,
    private companiesService: CompaniesService,
    private snackBarService: SnackBarService,
    private spinner: NgxSpinnerService,
    private insurancePolicyService: InsurancePolicyService,
  ) { }

  ngOnInit(): void {
    const transportCompany = this.data && this.data.transportCompany;
    const insuranceInformation = transportCompany && transportCompany.insuranceInformation
      ? transportCompany.insuranceInformation
      : {};
    // undefined, null y false son equivalentes: rige la póliza de la transportadora.
    const policyByCompany = this.insurancePolicyService.appliesClientPolicy(transportCompany);

    this.form = new FormGroup({
      policyByCompany: new FormControl(policyByCompany),
      insuranceInformation: new FormGroup({
        companyId: new FormControl(insuranceInformation.companyId || '', [Validators.required, Validators.maxLength(10)]),
        companyName: new FormControl(insuranceInformation.companyName || '', Validators.required),
        policyNumber: new FormControl(insuranceInformation.policyNumber || '', Validators.required),
        expirationDate: new FormControl(this.parseExpirationDate(insuranceInformation.expirationDate), Validators.required),
      })
    });
  }

  fromNowFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const selectedDate = this.form && this.form.get('insuranceInformation.expirationDate').value;
    if (selectedDate && selectedDate.getTime && selectedDate.getTime() === date.getTime()) return true;
    return DateManager.isSameOrBefore(DateManager.setStartOfDay(new Date()), date);
  };

  get policyExpired(): boolean {
    const expirationDate = this.form && this.form.get('insuranceInformation.expirationDate').value;
    return !!(expirationDate && expirationDate.getTime && expirationDate.getTime() <= DateManager.setStartOfDay(new Date()).getTime());
  }

  save(): void {
    if (!this.isValidForm() || this.saving) return;

    const value = this.form.value;
    const body = {
      policyByCompany: value.policyByCompany,
      insuranceInformation: {
        ...value.insuranceInformation,
        expirationDate: DateManager.dateToString(value.insuranceInformation.expirationDate, 'DD/MM/YYYY'),
      }
    };

    this.saving = true;
    this.spinner.show();
    this.companiesService.updateSaasTransportCompanyInsuranceInformation(
      this.data.transportCompany.companyId,
      body
    ).toPromise().then(() => {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_UPDATE_SUCCESS, undefined, 'success');
      this.dialogRef.close({ updated: true });
    }).catch(() => {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      this.saving = false;
    }).finally(() => this.spinner.hide());
  }

  private isValidForm(): boolean {
    const insuranceInformation = this.form.get('insuranceInformation');
    if (this.utils.errorMessagesCustomized(insuranceInformation.get('companyId'), 'nit de la entidad emisora', null, 10)) return false;
    if (this.utils.errorMessagesCustomized(insuranceInformation.get('companyName'), 'nombre de la entidad emisora')) return false;
    if (this.utils.errorMessagesCustomized(insuranceInformation.get('policyNumber'), 'no. de póliza')) return false;
    if (this.utils.errorMessagesCustomized(insuranceInformation.get('expirationDate'), 'fecha de expiración')) return false;
    return true;
  }

  private parseExpirationDate(expirationDate: string): Date | null {
    return this.insurancePolicyService.parseExpirationDate(expirationDate);
  }
}
